import { tw } from "@/utils";
import { ResourceIcon } from "./assets";
import { useEffect, useState } from "react";

import {
  getAgeFromBirthDate,
  getAgeFromIdNumber,
  getAlteredIdNumber,
} from "./utils";
import { PatientStatusInfo } from "./PatientStatusInfo";
import { MedicalHistoryInfo } from "./MedicalHistoryInfo";

import FollowUpIcon from "@/assets/followUpIcon.svg?react";
import AcceptIcon from "./assets/acceptIcon.svg?react";
import RejectIcon from "./assets/rejectIcon.svg?react";
import SendIcon from "./assets/sendIcon.svg?react";
import { useWindowStore } from "@/store/WindowWidth";
import { useGetPatientStatus, usePostRuquestDecision } from "./api";
import { defaultPatientStatus } from "./PatientStatusInfo";
import { RequiredResource } from "./types";
import { codeToName } from "@/data/codes";
import { useFollowUpHospitalStore } from "@/store/FollowUpHospital";
import { useGetTransferInfo } from "@/pages/CareInsight/CareInsightDetail/api";
import { useNavigate } from "react-router";

interface PatientInfoCardProps {
  requestId: string;
}

const identificationNumber = "881225-2341840";

export const PatientInfoCard = (props: PatientInfoCardProps) => {
  const { requestId } = props;
  const navigate = useNavigate();
  const width = useWindowStore((state) => state.width);
  const isButtonSmall = width < 1600;
  const { setFollowUpHospital } = useFollowUpHospitalStore();

  const { data, isSuccess, isLoading, isError } =
    useGetPatientStatus(requestId);
  const { mutate, isPending } = usePostRuquestDecision(requestId);
  const {
    data: transferData,
    isSuccess: transferIsSuccess,
    isLoading: transferIsLoading,
    isError: transferIsError,
  } = useGetTransferInfo(requestId);

  const [isIdNumberVisible, setIsIdNumberVisible] = useState<boolean>(false);

  useEffect(() => {
    if (isSuccess) {
      const followUpHospital = data.result.data.medicalHistory.find(
        (history) => history.isFollowUp === true
      )?.hospitalName;
      if (followUpHospital) {
        setFollowUpHospital(followUpHospital);
      }
    }

    return () => {
      setFollowUpHospital(null);
    };
  }, [isSuccess, data]);

  return (
    <div
      className={tw("w-full h-full bg-white shadow-patient-box rounded-[16px]")}
    >
      <div
        className={tw(
          "flex items-center justify-between w-full h-[72px] px-[24px] py-[20px]",
          "border-solid border-b-[1px] border-gray-g30"
        )}
      >
        <div className="flex items-center gap-[18px]">
          {/* 이름 */}
          <div className="text-[22px] font-semibold text-gray-g500">
            {isSuccess && data.result.data.patient.name}
          </div>
          {/* 추후 생년월일을 통해 계산 */}
          <div className="text-lg font-medium text-gray-g200">
            {isSuccess && getAgeFromBirthDate(data.result.data.patient.birthDt)
              ? `만 ${getAgeFromBirthDate(data.result.data.patient.birthDt)}세`
              : "나이 정보 없음"}
          </div>
        </div>
        {/* 주민등록번호 */}
        {/* <div className="text-base font-regular text-gray-g300">
            {identificationNumber}
          </div> */}
        {/* <button
            className={tw(
              "w-[54px] h-[20px]",
              "border-solid border-[1px] border-gray-g30 rounded-[6px]",
              "text-sm font-medium text-gray-g100"
            )}
            onClick={() => setIsIdNumberVisible((prev) => !prev)}
          >
            {isIdNumberVisible ? "보기" : "숨기기"}
          </button> */}
        {isSuccess &&
          transferIsSuccess &&
          (!transferData.result.data.requestStatus.includes("transfer") ? (
            <div className="flex items-center gap-[10px] w-fit">
              <button
                className={tw(
                  "flex items-center justify-center gap-[10px]",
                  "w-[150px] h-[44px] shadow-dicision-button bg-green rounded-[8px]",
                  "hover:bg-[#1DA363] active:bg-[#14814D]",
                  isButtonSmall && "w-[130px]"
                )}
                onClick={() => {
                  mutate(
                    { requestStatus: "accepted" },
                    {
                      onSuccess() {
                        // alert("수용");
                        navigate("/careInsight");
                      },
                      onError() {
                        alert("실패하였습니다. 다시 시도해주세요");
                      },
                    }
                  );
                }}
                disabled={isPending}
              >
                <AcceptIcon className="stroke-white" />
                <div className="text-base font-semibold text-white">수용</div>
              </button>
              <button
                className={tw(
                  "flex items-center justify-center gap-[10px] ",
                  "w-[150px] h-[44px] shadow-dicision-button bg-red rounded-[8px]",
                  "hover:bg-[#F64B52] active:bg-[#E0383F]",
                  isButtonSmall && "w-[130px]"
                )}
                onClick={() => {
                  mutate(
                    { requestStatus: "rejected", rejectReason: "거절" },
                    {
                      onSuccess() {
                        navigate("/careInsight");
                      },
                      onError() {
                        alert("실패하였습니다. 다시 시도해주세요");
                      },
                    }
                  );
                }}
                disabled={isPending}
              >
                <RejectIcon className="stroke-white" />
                <div className="text-base font-semibold text-white">미수용</div>
              </button>
            </div>
          ) : transferData.result.data.requestStatus ===
            "transfer_completed" ? (
            <div
              className={tw(
                "flex items-center justify-center",
                "w-[150px] h-[44px] bg-[rgba(45,185,118,0.10)]",
                "border-solid border-[1px] border-green rounded-[8px]",
                "text-base font-semibold text-[#257D53]"
              )}
            >
              이송 완료
            </div>
          ) : (
            <button
              className={tw(
                "flex items-center gap-[10px]",
                "w-fit h-[44px] px-[16px] shadow-dicision-button bg-purple-100 rounded-[8px]",
                "hover:bg-[#4E48DB] active:bg-[#3D37BC]",
                "text-base font-semibold text-white"
              )}
              onClick={() => {
                navigate(`/transferring/${requestId}`);
              }}
            >
              <SendIcon />
              이송 상황으로 이동
            </button>
          ))}
      </div>
      <div className="w-full h-[calc(100%-72px)] overflow-auto px-[18px] py-[16px]">
        {/* 주호소 증상, 활력징후 */}
        <PatientStatusInfo
          data={
            isSuccess
              ? data?.result.data.status[data?.result.data.status.length - 1]
                  .patientStatus
              : defaultPatientStatus
          }
        />
        {/* 구분선 */}
        <div className="w-full h-[1px] my-[20px] bg-gray-g30" />
        {/* 필요 진료 자원 */}
        <div className="w-full ">
          <div className="flex items-center gap-[8px]">
            <ResourceIcon />
            <div className="text-base font-semibold text-gray-g200">
              필요 진료 자원
            </div>
          </div>
          <div className="w-full mt-[10px]">
            {convertResourceToDisplayData(
              data?.result.data.status[data?.result.data.status.length - 1]
                .requiredResource
            ).map((resource) => (
              <div
                key={resource.label}
                className="flex items-center h-[40px] gap-[8px]"
              >
                <div className="w-[80px] text-base font-medium text-gray-g100">
                  {resource.label}
                </div>
                {resource.value ? (
                  resource.value.map((text: string) => (
                    <div className="flex items-center gap-[8px]">
                      <div className="text-base font-medium text-purple-100">
                        {resource.label !== "장비"
                          ? codeToName[text].split("] ")[
                              codeToName[text].split("] ").length - 1
                            ]
                          : codeToName[text]}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-base font-medium text-gray-g75">-</div>
                )}
              </div>
            ))}
          </div>
        </div>
        {/* 구분선 */}
        <div className="w-full h-[1px] my-[20px] bg-gray-g30" />
        {/* 진료이력 */}
        <div className="w-full h-fit">
          <div className="flex items-center gap-[20px]">
            <div className="flex items-center gap-[8px]">
              <ResourceIcon />
              <div className="text-base font-semibold text-gray-g200">
                진료 이력
              </div>
            </div>
            <div className="flex items-center gap-[4px]">
              <FollowUpIcon className="w-[14px] h-[14px]" />
              <div className="text-sm font-medium text-purple-100">
                유사증상 진단 병원
              </div>
            </div>
          </div>
          <MedicalHistoryInfo
            data={isSuccess ? data.result.data.medicalHistory : []}
          />
        </div>
      </div>
    </div>
  );
};

const resourceLabels: Record<keyof RequiredResource, string> = {
  operations: "수술",
  beds: "병상",
  equipments: "장비",
};

export const convertResourceToDisplayData = (resource?: RequiredResource) => {
  if (!resource) {
    return [{ label: "수술" }, { label: "병상" }, { label: "장비" }];
  }

  return (Object.keys(resourceLabels) as (keyof RequiredResource)[]).map(
    (key) => {
      const values = resource[key];
      return {
        label: resourceLabels[key],
        ...(values?.length ? { value: values } : {}),
      };
    }
  );
  // .filter((item) => Object.keys(item).length > 1); // value가 없으면 제외
};
